'use client'

import React, { useEffect } from 'react'
import { useRouter } from 'next/navigation'

import { Spinner, useLocale } from '@rewardops/ui'
import { Section } from '@rewardops/utils'

import { useApi } from '~/components/hooks/use-api'
import NotFound from '~/components/not-found/not-found'
import { useTranslation } from '~/app/i18n/client'
import Breadcrumbs from '../../breadcrumbs'
import MapleButton from '../../components/button'
import { Steps } from '../../components/steps'

const MapleHowToEarnPage: React.FC = () => {
  const { t: translate } = useTranslation()
  const { locale } = useLocale()
  const { api } = useApi()
  const router = useRouter()

  const [steps, setSteps] = React.useState([])
  const [links, setLinks] = React.useState([])

  const { data, isLoading } = api.contentful.getById.useQuery(
    {
      type: 'page',
      key: 'how-to-earn',
      language: locale,
    },
    {
      enabled: Boolean(locale),
    },
  )

  const breadcrumbs = [
    { label: translate('home'), path: '/' },
    { label: translate('retailers.earn'), path: '/retailers' },
    { label: translate('retailers.howToEarn') },
  ]

  useEffect(() => {
    if (!isLoading && (data as any)?.sections?.length) {
      const stepperSections = (data as any)?.sections?.filter(
        (section: Section) => section?._type == 'stepper',
      )

      const linkSections = (data as any)?.sections?.filter(
        (section: any) => section?._type === 'legalLink',
      )

      if (stepperSections?.[0]?.steps?.length) {
        setSteps(stepperSections?.[0]?.steps)
      }

      if (linkSections?.length) {
        setLinks(linkSections)
      }
    }
  }, [isLoading])

  if (!isLoading && !Boolean(data)) {
    return <NotFound />
  }

  if (isLoading || !steps?.length) {
    return <Spinner className="mx-auto mt-4" />
  }

  const handleClick = (to: string, isRelativeUrl: boolean) => {
    if (isRelativeUrl) {
      return router.push(to)
    }

    window.open(to, '_blank')
  }

  return (
    <div className="flex justify-center">
      <div className="w-full p-8 lg:w-[1140px]">
        <Breadcrumbs crumbs={breadcrumbs} />

        <div className="mt-16 flex flex-col gap-16">
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-32">
            <div className="basis-2/4 text-5xl font-semibold leading-[120%] -tracking-[0.2px]">
              {translate('howToEarnPage.heading')}
            </div>
            <div className="basis-3/4 text-base text-gray-600">
              {translate('howToEarnPage.body')}
            </div>
          </div>

          <Steps steps={steps} />

          {links?.length ? (
            <div className="flex flex-col gap-8">
              <div className="text-3xl font-semibold">
                {translate('howToEarnPage.getStartedToday')}
              </div>
              <div className="flex gap-4">
                {links?.map(
                  (
                    link: {
                      name: string
                      id: string
                      to: string
                      isRelativeUrl: boolean
                    },
                    index: number,
                  ) => (
                    <MapleButton
                      id={link?.name}
                      buttonStyle={index % 2 == 0 ? 'primary' : 'secondary'}
                      link={link?.to}
                      buttonType="link"
                      onClick={() => handleClick(link?.to, link?.isRelativeUrl)}
                    >
                      {link?.name}
                    </MapleButton>
                  ),
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export { MapleHowToEarnPage }
